import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon, StarIcon } from 'lucide-react';
import BackButton from '../UI/BackButton';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
export interface CreativeArtist {
  id: string;
  name: string;
  title: string;
  specialties: string[];
  rating: number;
  reviewCount: number;
  imageUrl: string;
  available: boolean;
  priceRange: string;
}
export interface CreativeDirectoryPageProps {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  profileBasePath: string;
  artists: CreativeArtist[];
}
export const CreativeDirectoryPage: React.FC<CreativeDirectoryPageProps> = ({
  title,
  subtitle,
  icon,
  profileBasePath,
  artists
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const filtered = artists.filter(a => a.name.toLowerCase().includes(searchQuery.toLowerCase()) || a.specialties.some(s => s.toLowerCase().includes(searchQuery.toLowerCase())));
  return <div className={`min-h-screen py-8 ${dk.page}`} data-id="element-1326" data-mp="2lu5bifqnhvci">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1327" data-mp="2lvgg2hrx774o">
        {/* Back */}
        <div className="mb-6" data-id="element-1328" data-mp="2h0arfb74vecw">
          <BackButton data-id="element-1329" data-mp="2d123imyryap9" />
        </div>

        {/* Header */}
        <div className="text-center mb-12" data-id="element-1330" data-mp="2cypl68p3tybo">
          <div className={`inline-flex items-center justify-center h-16 w-16 rounded-2xl mb-4 ${rt.bgMedium}`} data-id="element-1331" data-mp="2828nonw0m7lx">
            <span className={rt.textPrimary} data-id="element-1332" data-mp="2311ecke850d5">{icon}</span>
          </div>
          <h1 className={`text-3xl font-bold mb-4 ${dk.textHeading}`} data-id="element-1333" data-mp="1y2rjyc0bq3sl">
            {title}
          </h1>
          <p className={`text-lg max-w-2xl mx-auto ${dk.textSecondary}`} data-id="element-1334" data-mp="1u2zg2clvkltk">
            {subtitle}
          </p>
        </div>

        {/* Search */}
        <div className="mb-8 max-w-lg mx-auto" data-id="element-1335" data-mp="3sf7xlxvg8k7h">
          <div className="relative" data-id="element-1336" data-mp="3oh0ewxhfd8tn">
            <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${dk.textFaint}`} data-id="element-1337" data-mp="3oduonex280wc" />
            <input type="text" placeholder="Search by name or specialty..." className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.input}`} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1338" data-mp="1qjqmo8g496do" />
          </div>
        </div>

        {/* Results count */}
        {searchQuery && <p className={`text-sm mb-4 ${dk.textMuted}`} data-id="element-1339" data-mp="29htcuqkeh9d2">
            {filtered.length} result{filtered.length !== 1 ? 's' : ''} for "
            {searchQuery}"
          </p>}

        {/* Grid */}
        {filtered.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1340" data-mp="20k109u62cftd">
            {filtered.map(artist => <div key={artist.id} className={`rounded-lg shadow-md overflow-hidden border ${dk.card}`} data-id="element-1341" data-mp="p2rjxgi8e3wa">
                <img src={artist.imageUrl} alt={artist.name} className="w-full h-48 object-cover" data-id="element-1342" data-mp="gk7dadbbe13k" />
                <div className="p-6" data-id="element-1343" data-mp="qj5ae4mt32y6">
                  <div className="flex justify-between items-start mb-2" data-id="element-1344" data-mp="qi3yjxxyucfe">
                    <h3 className={`text-lg font-semibold ${dk.textPrimary}`} data-id="element-1345" data-mp="lmeupiewy9da">
                      {artist.name}
                    </h3>
                    <div className="flex items-center gap-1" data-id="element-1346" data-mp="3tykkt6z3jeju">
                      <StarIcon className="h-4 w-4 text-yellow-400 fill-yellow-400" data-id="element-1347" data-mp="3p3ewy2m4vpt4" />
                      <span className={`text-sm ${dk.textSecondary}`} data-id="element-1348" data-mp="7mdrvxf38rv6">
                        {artist.rating}
                      </span>
                      <span className={`text-xs ${dk.textFaint}`} data-id="element-1349" data-mp="2sstw5dhvxvz">
                        ({artist.reviewCount})
                      </span>
                    </div>
                  </div>
                  <p className={`text-sm mb-4 ${dk.textSecondary}`} data-id="element-1350" data-mp="3698c087ol8i2">
                    {artist.title}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-4" data-id="element-1351" data-mp="2ej22c6j4jeqw">
                    {artist.specialties.map(specialty => <span key={specialty} className={`px-2 py-0.5 text-xs rounded-full ${rt.badge}`} data-id="element-1352" data-mp="2ikdvun54ck4d">
                        {specialty}
                      </span>)}
                  </div>
                  <div className="flex items-center justify-between text-sm mb-4" data-id="element-1353" data-mp="2x90hf6lca841">
                    <span className={artist.available ? 'text-green-600' : 'text-red-500'} data-id="element-1354" data-mp="328mthcpflib9">
                      {artist.available ? '● Available for Work' : '● Currently Unavailable'}
                    </span>
                    <span className={`font-medium ${dk.textSecondary}`} data-id="element-1355" data-mp="1bwpb5uveqq3b">
                      {artist.priceRange}
                    </span>
                  </div>
                  <div className="flex gap-2" data-id="element-1356" data-mp="zlfyq0bjbidg">
                    <button className={`flex-1 px-4 py-2 rounded-lg transition-colors text-sm font-medium text-white ${rt.primaryButton}`} data-id="element-1357" data-mp="zkmxtfz9bn38">
                      Contact
                    </button>
                    <Link to={`${profileBasePath}/${artist.id}`} className={`flex-1 px-4 py-2 rounded-lg transition-colors text-sm font-medium text-center border ${rt.outlineButton}`} data-id="element-1358" data-mp="1nc241ju5mojr">
                      View Portfolio
                    </Link>
                  </div>
                </div>
              </div>)}
          </div> : <div className={`text-center py-16 rounded-xl border-2 border-dashed ${dk.emptyState}`} data-id="element-1359" data-mp="2wqdc00p87mq">
            <SearchIcon className={`h-10 w-10 mx-auto mb-3 ${dk.textFaint}`} data-id="element-1360" data-mp="3u0f3bmjm0ar3" />
            <h3 className={`text-lg font-medium mb-1 ${dk.textHeading}`} data-id="element-1361" data-mp="3vk701tzyy35p">
              No results found
            </h3>
            <p className={dk.textMuted} data-id="element-1362" data-mp="4ccd9rqj9z57">Try a different name or specialty</p>
            <button onClick={() => setSearchQuery('')} className={`mt-4 text-sm font-medium ${rt.accentLink}`} data-id="element-1363" data-mp="3mhflhlbsqr9u">
              Clear search
            </button>
          </div>}
      </div>
    </div>;
};